import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, type Job } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useJobs } from '../components/JobToast';

interface Source {
  id: number;
  platform: string;
  handle: string;
  title: string | null;
  message_count: number;
  created_at: string;
  last_ingest_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  health: string;
  case_id: string | null;
  created_by_name: string | null;
}

interface PreviewMessage {
  id: string;
  date_utc: string;
  text: string | null;
  permalink: string;
  has_media: boolean;
  media_label: string | null;
}

interface PreviewResult {
  handle: string;
  title: string | null;
  link: string;
  messages: PreviewMessage[];
}

export function SourcesPage() {
  const { trackJob } = useJobs();
  const [sources, setSources] = useState<Source[]>([]);
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const load = async () => {
    const data = await api.get<{ sources: Source[] }>('/api/sources');
    setSources(data.sources);
  };

  useEffect(() => {
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'));
  }, []);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!handle.trim()) return;
    await api.post('/api/sources', { handle: handle.trim(), platform: 'telegram' });
    setHandle('');
    await load();
  };

  const remove = async (id: number) => {
    if (!confirm('Bron en alle berichten verwijderen?')) return;
    await api.delete(`/api/sources/${id}`);
    await load();
  };

  const refreshOne = async (s: Source) => {
    setBusyId(s.id);
    setError(null);
    try {
      const { job } = await api.post<{ job: Job }>(`/api/sources/${s.id}/refresh`, { limit: 100 });
      const done = await trackJob(job.id, `Vernieuwen ${s.handle}`);
      if (done.status === 'failed') setError(done.error || 'Vernieuwen mislukt');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const refreshAll = async () => {
    setBusyId('all');
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<{ queued: number }>('/api/sources/refresh-all', { limit: 50 });
      setInfo(`${res.queued} bronnen in de wachtrij voor update (alerts vuren bij nieuwe berichten).`);
    } finally {
      setBusyId(null);
    }
  };

  const runPreview = async (target: string) => {
    setBusyId(`preview:${target}`);
    setError(null);
    setPreview(null);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs/preview', { handle: target, limit: 8 });
      const done = await trackJob(job.id, `Preview ${target}`);
      if (done.status === 'failed') {
        setError(done.error || 'Preview mislukt');
        return;
      }
      setPreview(done.result as PreviewResult);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1>Bronnen</h1>
      <p className="page-lead">
        Opgeslagen kanalen. Preview om te beoordelen of een bron nuttig is. Bronnen worden automatisch
        periodiek bijgewerkt (standaard elk uur) zodat alerts op nieuwe berichten blijven werken.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void add(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Handle toevoegen</label>
          <input
            className="input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@kanaal"
          />
        </div>
        <button className="btn" type="submit">
          Toevoegen
        </button>
        {handle.trim() && (
          <button
            className="btn btn-ghost"
            type="button"
            disabled={busyId !== null}
            onClick={() => void runPreview(handle.trim())}
          >
            Preview
          </button>
        )}
        <button
          className="btn btn-ghost"
          type="button"
          disabled={busyId !== null || sources.length === 0}
          onClick={() => void refreshAll()}
        >
          {busyId === 'all' ? 'Bezig…' : 'Alles vernieuwen'}
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert ok">{info}</div>}

      {preview && (
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="row" style={{ marginBottom: 8 }}>
            <div>
              <strong>{preview.handle}</strong>
              {preview.title && <span className="muted"> · {preview.title}</span>}
            </div>
            <div className="spacer" />
            <a href={preview.link} target="_blank" rel="noopener noreferrer">
              Open op Telegram
            </a>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setPreview(null)}>
              Sluiten
            </button>
          </div>
          <div className="msg-list">
            {preview.messages.map((m) => (
              <article className="msg" key={m.id}>
                <div className="msg-head">
                  <span>{formatDateTime(m.date_utc)}</span>
                  {m.has_media && <span className="badge">{m.media_label || 'Media'}</span>}
                </div>
                <pre className="msg-text">{m.text || '[leeg]'}</pre>
              </article>
            ))}
            {preview.messages.length === 0 && <div className="empty">Geen recente berichten zichtbaar.</div>}
          </div>
        </div>
      )}

      <div className="panel" style={{ marginTop: 14, padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Handle</th>
              <th>Berichten</th>
              <th>Status</th>
              <th>Laatst bijgewerkt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.handle}</strong>
                  {s.title && <div className="muted">{s.title}</div>}
                  {s.case_id && <div className="muted">Zaak: {s.case_id.slice(0, 8)}…</div>}
                </td>
                <td>
                  <Link to={`/search?source=${encodeURIComponent(s.handle)}&browse=1`}>
                    {s.message_count}
                  </Link>
                </td>
                <td>
                  <span className={`badge ${s.health === 'ok' ? 'ok' : s.health === 'error' ? 'danger' : 'warn'}`}>
                    {s.health || 'unknown'}
                  </span>
                  {s.last_error && <div className="muted" style={{ fontSize: '0.75rem' }}>{s.last_error}</div>}
                </td>
                <td className="muted">
                  {s.last_ingest_at ? formatDateTime(s.last_ingest_at) : 'Nog nooit'}
                </td>
                <td>
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void runPreview(s.handle)}
                    >
                      Preview
                    </button>
                    <Link className="btn btn-ghost btn-sm" to={`/search?source=${encodeURIComponent(s.handle)}&browse=1`}>
                      Berichten
                    </Link>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void refreshOne(s)}
                    >
                      {busyId === s.id ? '…' : 'Vernieuwen'}
                    </button>
                    <Link className="btn btn-ghost btn-sm" to={`/ingest?handle=${encodeURIComponent(s.handle)}`}>
                      Inladen
                    </Link>
                    <button className="btn btn-danger btn-sm" type="button" onClick={() => void remove(s.id)}>
                      Verwijderen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Nog geen bronnen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
